import { useEffect } from 'react'
import L from 'leaflet'
import { MapContainer, TileLayer, Marker, Popup, useMap } from 'react-leaflet'
import MarkerClusterGroup from 'react-leaflet-cluster'
import type { NocDeviceRow } from '@/services/admin.service'
import 'leaflet/dist/leaflet.css'
import './DeviceMap.css'

// Color-coded marker icons
const iconMap = {
  green: L.icon({
    iconUrl: 'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-green.png',
    shadowUrl: 'https://unpkg.com/leaflet@1.7.1/dist/images/marker-shadow.png',
    iconSize: [25, 41],
    iconAnchor: [12, 41],
    popupAnchor: [1, -34],
    shadowSize: [41, 41]
  }),
  yellow: L.icon({
    iconUrl: 'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-yellow.png',
    shadowUrl: 'https://unpkg.com/leaflet@1.7.1/dist/images/marker-shadow.png',
    iconSize: [25, 41],
    iconAnchor: [12, 41],
    popupAnchor: [1, -34],
    shadowSize: [41, 41]
  }),
  orange: L.icon({
    iconUrl: 'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-orange.png',
    shadowUrl: 'https://unpkg.com/leaflet@1.7.1/dist/images/marker-shadow.png',
    iconSize: [25, 41],
    iconAnchor: [12, 41],
    popupAnchor: [1, -34],
    shadowSize: [41, 41]
  }),
  red: L.icon({
    iconUrl: 'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-red.png',
    shadowUrl: 'https://unpkg.com/leaflet@1.7.1/dist/images/marker-shadow.png',
    iconSize: [25, 41],
    iconAnchor: [12, 41],
    popupAnchor: [1, -34],
    shadowSize: [41, 41]
  }),
  grey: L.icon({
    iconUrl: 'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-grey.png',
    shadowUrl: 'https://unpkg.com/leaflet@1.7.1/dist/images/marker-shadow.png',
    iconSize: [25, 41],
    iconAnchor: [12, 41],
    popupAnchor: [1, -34],
    shadowSize: [41, 41]
  })
}

interface LocatedDevice extends NocDeviceRow {
  lat: number
  long: number
}

// Match old portal battery color rules
const getBatteryColor = (batteryLevel?: string | null): keyof typeof iconMap => {
  if (!batteryLevel) return 'grey'
  const n = parseInt(batteryLevel, 10)
  if (Number.isNaN(n)) return 'grey'
  if (n > 85) return 'green'
  if (n > 75) return 'yellow'
  if (n > 55) return 'orange'
  return 'red'
}

const formatTimeAgo = (dateString?: string | null): string => {
  if (!dateString) return 'No Data'
  const date = new Date(dateString)
  const now = new Date()
  const diffMs = now.getTime() - date.getTime()
  const diffMins = Math.floor(diffMs / 60000)
  const diffHours = Math.floor(diffMins / 60)
  const diffDays = Math.floor(diffHours / 24)
  if (diffDays > 0) return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`
  if (diffHours > 0) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`
  if (diffMins > 0) return `${diffMins} minute${diffMins > 1 ? 's' : ''} ago`
  return 'Just now'
}

const FitBounds = ({ devices }: { devices: LocatedDevice[] }) => {
  const map = useMap()
  useEffect(() => {
    if (devices.length === 0) return
    const bounds = L.latLngBounds(devices.map(d => [d.lat, d.long]))
    map.fitBounds(bounds.pad(0.05), { padding: [10, 10], maxZoom: 15 })
  }, [devices, map])
  return null
}

interface NocDeviceMapProps {
  devices: NocDeviceRow[]
  height?: string
}

export const NocDeviceMap = ({ devices, height = '600px' }: NocDeviceMapProps) => {
  const located: LocatedDevice[] = devices.filter(
    (d): d is LocatedDevice =>
      typeof d.lat === 'number' &&
      typeof d.long === 'number' &&
      !Number.isNaN(d.lat) &&
      !Number.isNaN(d.long) &&
      d.lat !== 0 &&
      d.long !== 0
  )

  if (located.length === 0) {
    return (
      <div
        className="flex items-center justify-center bg-muted/20 rounded-lg"
        style={{ height }}
      >
        <p className="text-sm text-muted-foreground">No devices with GPS locations found</p>
      </div>
    )
  }

  const center: [number, number] = [located[0].lat, located[0].long]

  return (
    <div className="relative w-full rounded-lg overflow-hidden border" style={{ height }}>
      <MapContainer center={center} zoom={5} style={{ height: '100%', width: '100%' }}>
        <TileLayer
          attribution="&copy; Esri"
          url="https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}"
          maxZoom={19}
        />
        <MarkerClusterGroup chunkedLoading maxClusterRadius={25}>
          {located.map((device) => {
            const color = getBatteryColor(device.battery_level)
            const batteryText = device.battery_level && !Number.isNaN(parseInt(device.battery_level, 10))
              ? `${parseInt(device.battery_level, 10)}%`
              : 'No Data'
            return (
              <Marker
                key={device.id}
                position={[device.lat, device.long]}
                icon={iconMap[color]}
              >
                <Popup>
                  <div className="p-2 min-w-[180px]">
                    <strong className="text-base">{device.name}</strong>
                    <hr className="my-2" />
                    <p className="text-sm">Battery: <strong>{batteryText}</strong></p>
                    <p className="text-sm">{formatTimeAgo(device.last_seen)}</p>
                    <a
                      href={`/devices/${device.id}`}
                      className="text-sm text-blue-600 hover:underline mt-2 inline-block"
                    >
                      View Device
                    </a>
                  </div>
                </Popup>
              </Marker>
            )
          })}
        </MarkerClusterGroup>
        <FitBounds devices={located} />
      </MapContainer>

      {/* Battery legend overlay — matches old portal */}
      <div className="absolute top-3 right-3 bg-white rounded-md p-3 shadow-md z-[1000] text-xs">
        <div className="text-center font-semibold text-muted-foreground mb-2 tracking-wide">
          BATTERY LEVEL
        </div>
        <div className="flex items-center gap-2 flex-wrap justify-center">
          <span className="inline-flex items-center gap-1">
            <span className="inline-block w-3 h-3 rounded-full bg-green-500" />
            85%+
          </span>
          <span className="inline-flex items-center gap-1">
            <span className="inline-block w-3 h-3 rounded-full bg-yellow-400" />
            75%+
          </span>
          <span className="inline-flex items-center gap-1">
            <span className="inline-block w-3 h-3 rounded-full bg-orange-500" />
            55%+
          </span>
          <span className="inline-flex items-center gap-1">
            <span className="inline-block w-3 h-3 rounded-full bg-red-500" />
            &lt; 55%
          </span>
          <span className="inline-flex items-center gap-1">
            <span className="inline-block w-3 h-3 rounded-full bg-gray-400" />
            No Data
          </span>
        </div>
      </div>
    </div>
  )
}
